<script setup lang="ts">
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { computed, reactive, ref } from 'vue'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { Search } from '@element-plus/icons-vue'
import type { IRepairActionFilters } from '@/types/rc/filters'
import { currencyFormatter } from '@/helpers/ep/formatters'
import { useDebounceFn } from '@vueuse/core'
import { PartType } from '@/types/rc/miners/PartType'
import RepairActionDialog from '@/components/rc/repair-actions/RepairActionDialog.vue'
import type { IRepairAction } from '@/types/rc/miners'
import { useRepairActions } from '@/composables/rc/repair-actions'

// DELETE mutation
const { deleteRepairActionMutation } = useRepairActions()

const searchQuery = ref<string>()
const upsertDialogVisible = ref<boolean>(false)
const repairActionBeingModified = ref<IRepairAction>()

const filters = reactive<IRepairActionFilters>({
  fragment: undefined,
  part_type: undefined,
})

const { getRepairActionsQuery } = useRepairActions()

const { isFetching, isError, isRefetching, data, error, refetch } = getRepairActionsQuery(filters)

// Compute message to display when table is empty.
const tableEmptyText = computed(() => {
  if (isFetching.value && !filters.fragment) return 'Loading...'
  if (isFetching.value && filters.fragment) return `Searching for ${filters.fragment}`
  if (filters.fragment && !isFetching.value) return `No results for "${filters.fragment}"`
  else return 'No data'
})

/**
 * Handles edit
 *
 * @param repairAction repair action to edit.
 */
const handleEdit = async (repairAction: IRepairAction) => {
  repairActionBeingModified.value = repairAction
  upsertDialogVisible.value = true
}

const debouncedSearch = useDebounceFn((query: string) => {
  filters.fragment = query
}, 500)
</script>

<template>
  <ViewLayout class="repair-actions">
    <template v-if="!isError" #controls>
      <el-button type="primary" :disabled="isFetching" @click="upsertDialogVisible = true"
        >Create Repair Charge</el-button
      >
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="repair-actions__filters">
        <el-input
          v-model="searchQuery"
          style="width: 232px"
          placeholder="Description"
          clearable
          :suffix-icon="Search"
          @update:model-value="debouncedSearch"
        />
        <el-select v-model="filters.part_type" clearable placeholder="Part Type" style="width: 232px">
          <el-option v-for="item in PartType" :key="item" :label="item" :value="item" />
        </el-select>
      </div>
      <el-table v-loading="isFetching" :data="data" :empty-text="tableEmptyText" stripe>
        <el-table-column prop="id" label="ID" :min-width="32" />
        <el-table-column prop="part_type" label="Part Type" :min-width="160" />
        <el-table-column prop="action" label="Description" :min-width="160" />
        <el-table-column prop="price" label="Price " :min-width="160" :formatter="currencyFormatter" />
        <el-table-column :min-width="70" align="right">
          <template #default="scope">
            <el-button link title="Edit" @click="handleEdit(scope.row as IRepairAction)">
              <Icon icon="material-symbols:edit-outline-rounded" inline height="20" />
            </el-button>
            <el-button
              link
              title="Delete"
              @click="
                DeleteConfirmationModal(
                  'Delete Repair Charge',
                  'Are you sure you want to delete this repair charge? This action cannot be undone',
                  scope.row.id,
                  deleteRepairActionMutation.mutateAsync
                )
              "
            >
              <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </template>
    <RepairActionDialog
      v-model="upsertDialogVisible"
      :repair-action-to-update="repairActionBeingModified"
      @close="repairActionBeingModified = undefined"
    />
  </ViewLayout>
</template>

<style scoped lang="scss">
.repair-actions {
  &__filters {
    display: flex;
    gap: var(--spacing-spacing-sm);
  }
}
</style>
